import { LegalPage } from "@/components/landing/LegalPage";

const sections = [
  {
    id: "overview",
    title: "Overview",
    body: (
      <>
        <p>
          ClinicFirst Inc. (&ldquo;Clinic OS&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) builds an operating system for clinics. This policy explains what data we collect, how we use it, and the rights you have over it.
        </p>
        <p>
          We treat your clinic&apos;s data — and your patients&apos; data — the way we&apos;d want ours treated. <strong>Your EMR is the source of truth.</strong> Clinic OS reads from it; we never edit or delete records inside it.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "What we collect",
    body: (
      <>
        <p>We collect only what we need to run Clinic OS for your clinic. Broadly:</p>
        <ul>
          <li><strong>Account data</strong> — clinic name, owner contact, billing details, team member emails &amp; roles.</li>
          <li><strong>Operational data</strong> — appointment metadata, call recordings (only if you enable Call IQ AI), unified-inbox messages, KAI conversations.</li>
          <li><strong>Patient data</strong> — only the fields surfaced by your EMR via our read-only sync (name, contact, appointment, encounter notes when authorised).</li>
          <li><strong>Usage data</strong> — pages visited inside Clinic OS, module engagement, errors and performance metrics.</li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    title: "How we use it",
    body: (
      <>
        <p>We use data only to deliver and improve Clinic OS:</p>
        <ul>
          <li>Run the platform &amp; the AI modules you&apos;ve enabled (KAI, Call IQ, Voice Agents, etc.).</li>
          <li>Provide reception, owner and clinician views, alerts, and reporting.</li>
          <li>Respond to support requests and keep the product working reliably.</li>
          <li>Aggregate, anonymised insights to improve model accuracy and product decisions.</li>
        </ul>
        <p>We never sell your data. We never share it with third parties for advertising.</p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Who we share with",
    body: (
      <>
        <p>We share data only with sub-processors required to deliver the service — all under data-processing agreements:</p>
        <ul>
          <li><strong>Cloud infrastructure</strong> (AWS, Google Cloud) for hosting and storage.</li>
          <li><strong>Communications</strong> (SMS / voice / email providers) when you use those modules.</li>
          <li><strong>EMR partners</strong> (Jane App, Juvonno) for read-only sync, where you authorise.</li>
          <li><strong>Payment processors</strong> for billing.</li>
        </ul>
        <p>A full, up-to-date list is available from <strong>privacy@clinicfirst.app</strong>.</p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>Your data is yours. At any time you can:</p>
        <ul>
          <li><strong>Export</strong> every record, message, conversation and report from Settings.</li>
          <li><strong>Correct or delete</strong> personal data — email <strong>privacy@clinicfirst.app</strong>.</li>
          <li><strong>Disconnect your EMR</strong> in one click. Your EMR records stay intact.</li>
          <li><strong>Close your account</strong> — we delete operational data within 30 days, retain only what law requires.</li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    title: "Retention",
    body: (
      <p>
        Operational data is retained while your account is active and for up to 30 days after closure. Audit logs and billing records are retained for up to 7 years to meet legal obligations. Aggregated, anonymised analytics may be retained indefinitely.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>Questions or requests? Reach our privacy team:</p>
        <ul>
          <li><strong>Email</strong> — privacy@clinicfirst.app</li>
          <li><strong>Mail</strong> — ClinicFirst Inc., Ottawa, ON, Canada</li>
        </ul>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <LegalPage
      testId="privacy-page"
      kicker="Legal · privacy policy"
      title="How we handle"
      highlight="your data."
      subtitle="Your clinic's data — and your patients' data — is yours. This page explains what we collect, why, and the controls you have over it."
      updated="Feb 2026"
      sections={sections}
    />
  );
}
